import React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import * as BreakdownActions from 'store/modules/bookingManager/subdomains/breakdown/actions';
import { PriceAsCentsInput } from '../stateful/PriceAsCentsInput';
import { IHeadlineLineItemBreakdownLineItem } from 'services/BookingManagerApi';
import { toClassName } from './helpers';
import { HidingTooltip } from 'ui/Tooltip';
import InfoIcon from 'ui/Icons/components/Info.component';
import { SvgIcon } from 'ui/SvgIcon';
import { ClosingProductsRow } from './ClosingProductsRow';
import { RemoveHeadlineButton } from './HeadlineActionButtons';
import { isNil } from 'lodash-es';

interface IProductBlock {
  pathPrefix?: string;
  className?: string;
  productType: string;
  lineItems: IHeadlineLineItemBreakdownLineItem[];
  bookingCurrencySymbol?: string | null;
  subTotalCents: number;
  includeTableHeaders?: boolean;
  allowNegative?: boolean;
  setHeadlineLineItemFieldAction: (setPath: string, value: string) => void;
  removeHeadlineLineItemAction: (removePath: string, index: number) => void;
  isPurchaseCostEditable: boolean;
  titleJSX: React.ReactElement;
  isEditable: boolean;
  isLatestVersion?: boolean;
  cta?: React.ReactElement;
  isTa: boolean;
  isSR: boolean;
  isAdmin: boolean;
  isFinanceUser: boolean;
  isFinanceWithCompletedStatus: boolean;
  isSales: boolean;
  isLiveRate?: boolean;
  titlePlaceholder?: string;
  tertiaryTextPlaceholder?: string;
  isAccommodationSubBlock?: boolean; // if this is true, we need to fire the accommodation modified when user changes prices
  isSalesCostEditable?: boolean;
}
import { theme } from '../../../tailwind.config';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import classNames from 'classnames';

// necessary because we dont have placeholder utility classes
const LineItemInput = styled.input`
  &::placeholder {
    font-style: italic;
  }
`;

export const ProductsBlock = (props: IProductBlock) => {
  const {
    titleJSX,
    pathPrefix = '',
    lineItems,
    includeTableHeaders = false,
    productType,
    subTotalCents,
    allowNegative,
  } = props;

  const isEditable = props.isEditable && !props.isLiveRate;
  const isInternalUser = props.isAdmin || props.isSR || props.isFinanceUser;
  const isPurchaseCostVisible = props.isSR || props.isFinanceUser;
  const isPurchaseCostDisabled = !props.isPurchaseCostEditable || props.isLatestVersion === false;

  const isSalesCostEditable = isNil(props.isSalesCostEditable) ? props.isEditable : props.isSalesCostEditable;

  const dispatch = useDispatch();

  return (
    <>
      <span className="block mb-10px mt-40px">{titleJSX}</span>
      {/* the actual line items */}
      <div className="flex flex-col gap-[20px]">
        {lineItems.length >= 1 &&
          lineItems.map((lineItem, index) => {
            // OWA-8928. isEditable inside item forces edition for this item in product blocks not editable
            const isThisItemEditable =
              isEditable === true || (props.isLatestVersion !== false && lineItem.isEditable === true && isInternalUser);
            const keyPrefix = Math.random();
            return (
              <div
                key={index}
                className={classNames('relative group grid gap-20px items-center', {
                  'grid-cols-[1fr_1fr_110px_110px_24px]': !(props.isTa || props.isSales),
                  'grid-cols-[1fr_1fr_110px_110px]': props.isTa || props.isSales,
                })}
              >
                {/* title */}
                <HidingTextTooltip tooltipContent={lineItem.title} position="bottom">
                  <LineItemInput
                    disabled={!isThisItemEditable}
                    className={classnames(
                      'focus:outline-gray-80 text-black name border text-base border-solid border-gray-40 p-2 w-full font-pt-sans',
                      {
                        'bg-gray-10': !isThisItemEditable,
                      }
                    )}
                    value={lineItem.title}
                    placeholder={props.titlePlaceholder || 'Line Item Title'}
                    onChange={event => {
                      props.setHeadlineLineItemFieldAction(
                        `${pathPrefix}${productType}.items[${index}].title`,
                        event.target.value
                      );
                    }}
                  />
                </HidingTextTooltip>

                {/* tertiary text */}
                <HidingTextTooltip tooltipContent={lineItem.tertiaryText} position="bottom">
                  <LineItemInput
                    disabled={!isThisItemEditable}
                    className={classnames(
                      'focus:outline-gray-80 text-black details border text-base border-solid border-gray-40 p-2 w-full font-pt-sans',
                      {
                        'bg-gray-10': !isThisItemEditable,
                      }
                    )}
                    value={lineItem.tertiaryText}
                    placeholder={props.tertiaryTextPlaceholder || 'Line Item Description'}
                    onChange={event => {
                      props.setHeadlineLineItemFieldAction(
                        `${pathPrefix}${productType}.items[${index}].tertiaryText`,
                        event.target.value
                      );
                    }}
                  />
                </HidingTextTooltip>

                {/* purchase cost */}
                <div className="relative">
                  {isPurchaseCostVisible && includeTableHeaders && (
                    <label className="absolute -top-[36px] right-0 uppercase font-pt-sans text-xs text-gray-100 font-bold self-end">
                      Purchase Cost
                    </label>
                  )}
                  {isPurchaseCostVisible && (
                    <PriceAsCentsInput
                      disabled={isPurchaseCostDisabled}
                      allowNegative={allowNegative}
                      key={`${keyPrefix}-${index}-purchaseCostCents`}
                      className={classnames(
                        `focus:outline-gray-80 bg-blue-10  text-black cost-before-purchase-cost border text-base text-right border-solid border-gray-40 p-2 w-full font-pt-sans`,
                        {
                          'bg-gray-10': isPurchaseCostDisabled,
                        }
                      )}
                      cents={lineItem.purchaseCostCents || 0}
                      onBlurUpdate={val => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].purchaseCostCents`,
                          val
                        );
                      }}
                      onChange={e => {
                        if (props.isAccommodationSubBlock) {
                          dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                        }
                      }}
                    />
                  )}
                </div>

                {/* cost to tp */}
                <div className="relative">
                  {includeTableHeaders && (
                    <label className="absolute -top-[36px] right-0 uppercase font-pt-sans text-xs text-gray-100 font-bold self-end">
                      Cost to TP
                    </label>
                  )}
                  <PriceAsCentsInput
                    disabled={!isSalesCostEditable || !props.isLatestVersion}
                    allowNegative={allowNegative}
                    key={`${keyPrefix}-${index}-saleCostCents`}
                    className={classnames(
                      'focus:outline-gray-80 text-black cost-to-ta border text-base text-right border-solid border-gray-40 p-2 w-full font-pt-sans',
                      {
                        'bg-gray-10': !isSalesCostEditable,
                      }
                    )}
                    cents={lineItem.saleCostCents || 0}
                    onBlurUpdate={val => {
                      props.setHeadlineLineItemFieldAction(
                        `${pathPrefix}${productType}.items[${index}].saleCostCents`,
                        val
                      );
                    }}
                    onChange={e => {
                      if (props.isAccommodationSubBlock) {
                        dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                      }
                    }}
                  />
                </div>

                {/* remove cta */}
                {isThisItemEditable && (
                  <span className="block">
                    <RemoveHeadlineButton
                      action={props.removeHeadlineLineItemAction}
                      pathPrefix={pathPrefix}
                      productType={productType}
                      index={index}
                    />
                  </span>
                )}
              </div>
            );
          })}
      </div>
      <ClosingProductsRow
        productDisplayType={productType}
        subTotalCents={subTotalCents}
        bookingCurrencySymbol={props.bookingCurrencySymbol}
        cta={props.cta}
        hasItems={false}
        isEditable={props.isEditable}
      />
    </>
  );
};

export const FinanceAdjustmentProductsBlock = (props: IProductBlock) => {
  const {
    titleJSX,
    pathPrefix = '',
    className = '',
    lineItems,
    includeTableHeaders = false,
    productType,
    allowNegative,
  } = props;

  const isEditable = props.isEditable && !props.isLiveRate;
  const isPurchaseCostVisible = props.isSR || props.isFinanceUser;

  let isSalesCostEditable = props.isEditable;
  if (props.isLiveRate) {
    isSalesCostEditable = isSalesCostEditable && (props.isAdmin || props.isSR || props.isFinanceWithCompletedStatus);
  }

  return (
    <div className={`product ${toClassName(props.productType)} ${className}`} key={props.productType}>
      <table className="w-full">
        <thead>
          <tr>
            <th className="title p-0 flex space-x-2 items-center">
              <span className="w-[150px]">{titleJSX}</span>
              <HidingTooltip
                renderTooltipContent={() => (
                  <p className="font-normal w-[350px] p-1 text-13px">
                    Only visible for Internal Users.
                    <br />
                    Only affect purchase cost (not included in invoices). <br />
                    Automatically linked to Finance Purchase information.
                  </p>
                )}
                position="bottom-right"
              >
                <span className="flex">
                  <SvgIcon
                    IconComponent={InfoIcon}
                    defaultFill={theme.colors['gray-40']}
                    hoverFill={theme.colors['gray-80']}
                    activeFill={theme.colors['gray-40']}
                    width="18px"
                    height="18px"
                  />
                </span>
              </HidingTooltip>
            </th>
            {!includeTableHeaders && (
              <React.Fragment>
                <th className=""></th>
                <th className="w-14% text-gray-darker text-xs"></th>
                <th className="w-14% text-gray-darker text-xs"></th>
              </React.Fragment>
            )}
            {includeTableHeaders && lineItems.length >= 1 && (
              <React.Fragment>
                <th className=""></th>
                {isPurchaseCostVisible && (
                  <th className="cost-before-discount w-14% text-gray-darker text-xs uppercase text-right pr-2 font-pt-sans">
                    Purchase Cost
                  </th>
                )}
                <th
                  colSpan={isPurchaseCostVisible ? 1 : 2}
                  className="cost-to-ta w-14% text-gray-darker text-xs uppercase text-right pr-4 font-pt-sans"
                >
                  Cost to TP
                </th>
              </React.Fragment>
            )}
            {isPurchaseCostVisible && <th></th>}
          </tr>
        </thead>

        <tbody>
          {lineItems.length <= 0 && (
            <tr>
              <td className="p-0 pt-2 pb-4"></td>
            </tr>
          )}
          {lineItems.length >= 1 &&
            lineItems.map((lineItem, index) => {
              const keyPrefix = Math.random();
              return (
                <tr key={index}>
                  <td className="p-0 pr-2 pb-4 pt-2 w-1/4">
                    <LineItemInput
                      disabled={true}
                      className={classnames(
                        'focus:outline-gray-80 text-black name border text-base border-solid border-gray-40 p-2 w-full font-pt-sans',
                        {
                          'bg-gray-10': true,
                        }
                      )}
                      value={'Breakdown Finance Adjustment'}
                      placeholder={props.titlePlaceholder || 'Line Item Title'}
                      onChange={event => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].title`,
                          event.target.value
                        );
                      }}
                    />
                  </td>
                  <td colSpan={1} className="pl-2 pr-2 pb-4 pt-2">
                    <LineItemInput
                      disabled={true}
                      className={classnames(
                        'focus:outline-gray-80 text-black details border text-base border-solid border-gray-40 p-2 w-full font-pt-sans',
                        {
                          'bg-gray-10': true,
                        }
                      )}
                      value={lineItem.title}
                      placeholder={'Breakdown Finance Adjustment'}
                      onChange={event => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].tertiaryText`,
                          event.target.value
                        );
                      }}
                    />
                  </td>

                  <td className="w-14% p-0 pl-2 pr-2 pb-4 pt-2">
                    {/* cost before discount price */}
                    <PriceAsCentsInput
                      disabled={true}
                      allowNegative={allowNegative}
                      key={`${keyPrefix}-${index}-purchaseCostCents`}
                      className={classnames(
                        'focus:outline-gray-80 bg-blue-10  text-black cost-before-purchase-cost border text-base text-right border-solid border-gray-40 p-2 w-full font-pt-sans',
                        {
                          'bg-gray-10': props.isPurchaseCostEditable === false,
                        }
                      )}
                      cents={lineItem.purchaseCostCents || 0}
                      onBlurUpdate={val => {
                        props.setHeadlineLineItemFieldAction(
                          `${pathPrefix}${productType}.items[${index}].purchaseCostCents`,
                          val
                        );
                      }}
                    />
                  </td>
                  <td className="w-14% p-0 pl-2 pr-2 pb-4 pt-2">{/* deliberately blank */}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};
